import React from 'react';
import { createPortal } from 'react-dom';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import type { BroadcastSegment } from 'types/interfaces/broadcast';
import './ConfirmBroadcastModal.css';

const SEGMENT_LABELS: Record<BroadcastSegment, string> = {
  all: 'All Users',
  elite: 'Elite Users',
  free: 'Free Users',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  recipientCount: number;
  emailCount: number;
  segment: BroadcastSegment;
}

const ConfirmBroadcastModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  recipientCount,
  emailCount,
  segment,
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="confirm-broadcast-modal-overlay" onClick={isLoading ? undefined : onClose}>
      <div className="confirm-broadcast-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-broadcast-modal-header">
          <h2 className="confirm-broadcast-modal-title">Send Broadcast Message?</h2>
          {!isLoading && (
            <button className="confirm-broadcast-close-button" onClick={onClose}>
              <img
                src={AssetUrls.icons.dropdowns.close}
                alt="Close"
                style={{ width: 16, height: 16 }}
              />
            </button>
          )}
        </div>

        <div className="confirm-broadcast-modal-content">
          <p className="confirm-question">
            You are about to send a broadcast to <strong>{recipientCount.toLocaleString()}</strong> {SEGMENT_LABELS[segment].toLowerCase()}.
          </p>

          <p className="confirm-question">
            <strong>{emailCount.toLocaleString()}</strong> of those users will also receive an email notification.
          </p>

          <div className="warning-box">
            <strong>Warning:</strong> This action cannot be undone. Messages will be delivered immediately to all recipients.
          </div>

          {isLoading && (
            <p className="info-text">
              Sending broadcast... This may take up to 30 seconds for large segments.
            </p>
          )}
        </div>

        <div className="confirm-broadcast-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? "Sending..." : `Send to ${recipientCount.toLocaleString()} Users`}
            color="purple"
            onPress={onConfirm}
            isLoading={isLoading}
            isDisabled={isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConfirmBroadcastModal;
